import * as Highcharts from 'highcharts';
import Drilldown from 'highcharts/modules/drilldown';
import { ReusableFunctions } from '../classes/reusable-functions';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import * as Highmaps from 'highcharts/highmaps';
import {
    IDonutChartSeriesData,
    ILoanData,
} from '../../historical-trends/interfaces/geographic-map-data';

Drilldown(Highmaps);
HighchartsMore(Highmaps);
HighchartsExporting(Highmaps);
HighchartsExportData(Highmaps);

import 'highcharts/modules/map';
import 'highcharts/modules/data';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import { HistoricalInsightGeographicComponent } from '../../historical-trends/components/historical-trends-insights/hist-insights-geo/historical-insights-geographic.component';
import { GeographicMapService } from '../../historical-trends/services/geographic-map.service';

export namespace GeographicCharts {
    export class GeographicChart {
        constructor(
            private reusableFunctions: ReusableFunctions,
            private _mapService: GeographicMapService
        ) {}

        getMapOptions(
            data: any[],
            seperators: any[],
            displayAs: string,
            mainThis: HistoricalInsightGeographicComponent
        ): Highmaps.Options {
            let _this = this;
            return {
                chart: {
                    map: 'countries/us/us-all',
                    height: 670,
                    events: {
                        drilldown: async function (e) {
                            if (!e.seriesOptions) {
                                const chart = this,
                                    mapKey = `countries/us/${e.point.properties.drilldown}-all`;
                                chart.showLoading(
                                    '<i class="fa fa-spinner fa-spin" style="font-size:48px"></i>'
                                );

                                // Load the drilldown map;
                                // TODO: store state-county data in the hmda database and retrive from there
                                const topology = await fetch(
                                    `https://code.highcharts.com/mapdata/${mapKey}.topo.json`
                                ).then((response) => response.json());
                                let data = Highcharts.geojson(topology);
                                let parameters = mainThis.parameters;

                                if (parameters.dataSource === 'HMDA') {
                                    parameters.geographySelection = e.point.properties.name;
                                } else {
                                    parameters.geographySelection = e.point.properties['hc-a2'];
                                }

                                _this._mapService.getStateMapData(parameters).subscribe(
                                    (res) => {
                                        mainThis.setMapChartData(
                                            data,
                                            res,
                                            parameters.geographySelection
                                        );
                                        chart.hideLoading();
                                        chart.addSeriesAsDrilldown(e.point, {
                                            name: e.point.name,
                                            type: 'map',
                                            data: data,
                                            dataLabels: {
                                                enabled: true,
                                                format: '{point.name}',
                                            },
                                        });
                                    },
                                    (error) => {
                                        chart.showLoading(`
                                            <i class="fa-light fa-face-frown"></i>
                                            Failed loading ${e.point.name}`);
                                    }
                                );
                                mainThis.getPieChartData();
                            }
                        },
                        drillup: async function (e) {
                            mainThis.parameters.geographySelection = 'USA';
                            mainThis.getPieChartData();
                        },
                    },
                },

                title: {
                    text: '',
                    useHTML: false,
                },
                credits: {
                    enabled: false,
                },
                exporting: {
                    enabled: false,
                },
                legend: {
                    layout: 'vertical',
                    align: 'right',
                    verticalAlign: 'middle',
                    width: '10%',
                },
                colorAxis: {
                    min: 0,
                    stops: [
                        [0, '#e9faf8'],
                        [0.67, '#41d6c3'],
                        [1, '#1f9283'],
                    ],
                },
                mapNavigation: {
                    enabled: true,
                    buttonOptions: {
                        verticalAlign: 'bottom',
                    },
                },
                plotOptions: {
                    map: {
                        states: {
                            hover: {
                                color: '#FFF9E7',
                            },
                        },
                    },
                },
                series: [
                    {
                        data: data,
                        type: 'map',
                        name: 'USA',
                        dataLabels: {
                            enabled: true,
                            useHTML: true,
                            style: {
                                fontSize: '16px',
                            },
                            formatter: function () {
                                return `<span style="color: #223540">${this.point.properties['hc-a2']}</span>`;
                            },
                        },
                    },
                    {
                        type: 'mapline',
                        data: seperators,
                        color: 'silver',
                        enableMouseTracking: false,
                        animation: {
                            duration: 500,
                        },
                    },
                ],
                drilldown: {
                    activeDataLabelStyle: {
                        color: '#FFFFFF',
                        textDecoration: 'none',
                        textOutline: '1px #000000',
                    },
                    drillUpButton: {
                        relativeTo: 'spacingBox',
                        position: {
                            x: 0,
                            y: 60,
                        },
                    },
                },
                tooltip: {
                    style: {
                        color: '#223540',
                        fontFamily: 'Roboto',
                        fontSize: '12',
                        height: 12,
                    },
                    outside: true,
                    borderColor: '#223540',
                    borderRadius: 6,
                    useHTML: true,
                    shared: true,
                    backgroundColor: '#FFFFFF',
                    formatter: function () {
                        if (this.point) {
                            let s: string = _this.createTooltipTableString(
                                this.point.properties.loanPurposeArray,
                                this.point.properties.loanTypeArray,
                                this.point.properties.occupancyTypeArray,
                                this.key,
                                this.point.properties.volume,
                                this.point.properties.units,
                                displayAs
                            );
                            return s;
                        }
                    },
                },
            } as Highmaps.Options;
        }

        createTooltipTableString(
            purpose: ILoanData[],
            type: ILoanData[],
            occupancy: ILoanData[],
            key: string,
            volume: string,
            unit: string,
            displayAs
        ) {
            let getProperTitleFromAbbr =
                this.reusableFunctions.getProperTitleFromAbbrLoanTypeLoanPurpose;
            let s: string;
            let stringArr: string[] = [];
            // start it with top info
            s = `<b>${key}</b><table>
                        <tr>
                          <td align='left' style='padding:0'>Total Volume: </td>
                          <td align='right'>${volume}</td>
                        </tr>
                        <tr>
                          <td align='left' style='padding:0'>Total Units: </td>
                          <td align='right'>${unit}</td>
                        </tr>
                        <tr class="blank-row"><td colspan="2"></td></tr>`;
            stringArr.push(s);
            //for each type of obj group
            type.forEach((elem) => {
                if (displayAs.toLowerCase() === 'volume') {
                    s = `<tr>
               <td align='left' style='color: ${elem.color}' >${getProperTitleFromAbbr(
                        elem.abbr
                    )}: </td>
               <td align='right'>${elem.volumePercent.toFixed(2)}%</td>
             </tr>`;
                } else {
                    s = `<tr>
               <td align='left' style='color: ${elem.color}' >${getProperTitleFromAbbr(
                        elem.abbr
                    )}: </td>
               <td align='right'>${elem.unitPercent.toFixed(2)}%</td>
             </tr>`;
                }
                stringArr.push(s);
            });
            stringArr.push(`<tr class="blank-row"><td colspan="2"></td></tr>`);
            purpose.forEach((elem) => {
                if (displayAs.toLowerCase() === 'volume') {
                    s = `<tr>
               <td align='left' style='color: ${elem.color}' >${getProperTitleFromAbbr(
                        elem.abbr
                    )}: </td>
               <td align='right'>${elem.volumePercent.toFixed(2)}%</td>
             </tr>`;
                } else {
                    s = `<tr>
               <td align='left' style='color: ${elem.color}' >${getProperTitleFromAbbr(
                        elem.abbr
                    )}: </td>
               <td align='right'>${elem.unitPercent.toFixed(2)}%</td>
             </tr>`;
                }
                stringArr.push(s);
            });
            stringArr.push(`<tr class="blank-row"><td colspan="2"></td></tr>`);
            occupancy.forEach((elem) => {
                if (displayAs.toLowerCase() === 'volume') {
                    s = `<tr>
               <td align='left' style='color: ${elem.color}' >${getProperTitleFromAbbr(
                        elem.abbr
                    )}: </td>
               <td align='right'>${elem.volumePercent.toFixed(2)}%</td>
             </tr>`;
                } else {
                    s = `<tr>
               <td align='left' style='color: ${elem.color}' >${getProperTitleFromAbbr(
                        elem.abbr
                    )}: </td>
               <td align='right'>${elem.unitPercent.toFixed(2)}%</td>
             </tr>`;
                }
                stringArr.push(s);
            });
            // add break string at end of object
            stringArr.push(`</table>`);
            var str = stringArr.join(' ');
            return str;
        }

        public createDonutChart(
            titleText: string = null,
            seriesData: IDonutChartSeriesData[],
            total: string,
            assemblage: string
        ): Highcharts.Options {
            const _this = this;
            let assemblageFormattedForTooltip = assemblage.toLowerCase();
            assemblageFormattedForTooltip =
                assemblageFormattedForTooltip[0].toUpperCase() +
                assemblageFormattedForTooltip.slice(1);
            return {
                chart: {
                    type: 'pie',
                    height: 333,
                },
                credits: {
                    enabled: false,
                },
                title: {
                    text: titleText,
                    align: 'left',
                    style: {
                        fontSize: '23px',
                    },
                },
                legend: {
                    align: 'left',
                    verticalAlign: 'top',
                    y: 25,
                    itemStyle: {
                        fontWeight: 'Regular',
                        fontSize: '13px',
                    },
                    floating: true,
                    layout: 'horizontal',
                },
                subtitle: {
                    text: `<div><div style="font-size:36px; line-height: 52px; font-weight: bold;">
                    ${total}</div>
                  <div style="font-size:14px;  line-height: 19px; ">
                    ${assemblage}
                  </div></div>`,
                    useHTML: true,
                    align: 'center',
                    x: -3,
                    y: 150,
                    floating: true,
                    style: {
                        fontFamily: 'roboto',
                        color: '#333333',
                        textAlign: 'center',
                    },
                },
                exporting: {
                    enabled: false,
                },
                tooltip: {
                    backgroundColor: 'rgba(255,255,255,0)',
                    useHTML: true,
                    padding: 1,
                    formatter: function () {
                        return `<div class="donut-tooltip-map"><b>${
                            this.key
                        }</b><table style="z-index: 9999"><tr><td>${
                            this.series.name
                        }:</td><td style="text-align:right"><b>${this.percentage.toFixed(
                            1
                        )}%</b></td></tr>
                            <tr><td>${assemblageFormattedForTooltip}:</td><td style="text-align:right"><b>${
                            assemblage.toLowerCase() === 'volume' ? '$' : ''
                        }${_this.reusableFunctions.formatLargeNumber(
                            this.y
                        )}</b></td></tr></table><div>`;
                    },
                },
                plotOptions: {
                    pie: {
                        allowPointSelect: true,
                        dataLabels: {
                            enabled: false,
                        },
                        showInLegend: true,
                        size: '95%',
                    },
                },
                series: [
                    {
                        name: 'Percentage',
                        colorByPoint: true,
                        type: null, //was 'pie'
                        data: seriesData,
                    },
                ],
            } as Highcharts.Options;
        }
    }
}
